/**
 * useSuperadmin — is the signed-in user an admin?
 *
 * ONE PREDICATE (2026-08-09). This now calls `public.caller_is_superadmin()`,
 * which reads `public.user_roles` and nothing else. It used to query user_roles
 * directly from the browser, which was the right SOURCE but a second READER:
 * the RPC and this hook could drift, and every other admin check in the app was
 * a third and fourth way of asking (profiles.is_superadmin, and a hardcoded uuid
 * compared by hand in ~97 places).
 *
 * Roles are NEVER stored on profiles — a column on a row the user can sometimes
 * touch is a privilege-escalation surface. profiles.is_superadmin is retired as
 * _retired_is_superadmin_20260809.
 */

import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { isAdmin } from "@/lib/isAdmin";

interface UseSuperadminReturn {
  isSuperadmin: boolean;
  loading: boolean;
}

export function useSuperadmin(): UseSuperadminReturn {
  const [isSuperadmin, setIsSuperadmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;

    const check = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          if (alive) { setIsSuperadmin(false); setLoading(false); }
          return;
        }
        const ok = await isAdmin();
        if (alive) setIsSuperadmin(ok);
      } catch (error) {
        console.error("Error checking superadmin status:", error);
        if (alive) setIsSuperadmin(false);
      } finally {
        if (alive) setLoading(false);
      }
    };

    void check();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      void check();
    });

    return () => {
      alive = false;
      subscription.unsubscribe();
    };
  }, []);

  return { isSuperadmin, loading };
}
